/**
 * Query and mutation hooks for the Phase 2 surface.
 *
 * Every key starts with `['org', orgId, …]` like the rest of the cache, so
 * that signing out drops all of it at once (src/auth/session.tsx).
 *
 * A mutation refreshes what it changed and nothing is updated optimistically.
 * Moving a card, merging, filing an issue: the server decides whether it
 * happened and what the result is, and the page shows that answer rather than
 * the one it hoped for.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import * as api from './operations';
import type { ApiError } from './problem';
import { retryPolicy } from './queries';
import type { RepoScope } from './repoQueries';

const repoKey = (s: RepoScope) => ['org', s.orgId, 'repository', s.repoId] as const;
const pullKey = (s: RepoScope, n: number) => [...repoKey(s), 'pull', n] as const;
const issueKey = (s: RepoScope, n: number) => [...repoKey(s), 'issue', n] as const;
const projectKey = (orgId: string, projectId: string) =>
  ['org', orgId, 'project', projectId] as const;

function useQ<T>(
  key: readonly unknown[],
  fn: () => Promise<T>,
  enabled = true,
  staleTime = 15_000,
): UseQueryResult<T, ApiError> {
  return useQuery<T, ApiError>({
    queryKey: key,
    queryFn: fn,
    enabled,
    staleTime,
    retry: retryPolicy,
  });
}

/** A mutation that refreshes every query under the given key prefixes. */
function useRefreshing<V, R>(
  fn: (vars: V) => Promise<R>,
  prefixes: (readonly unknown[])[],
): UseMutationResult<R, ApiError, V> {
  const client = useQueryClient();
  return useMutation<R, ApiError, V>({
    mutationFn: fn,
    onSettled: async () => {
      for (const queryKey of prefixes) {
        await client.invalidateQueries({ queryKey });
      }
    },
  });
}

/* ---------------------------------------------------------------------- *
 * Teams
 * ---------------------------------------------------------------------- */

export function useTeams(orgId: string | undefined) {
  return useQ(['org', orgId ?? '', 'teams'], () => api.listTeams(orgId ?? ''), Boolean(orgId));
}

export function useTeamMembers(orgId: string | undefined, teamId: string | undefined) {
  return useQ(
    ['org', orgId ?? '', 'teams', teamId ?? '', 'members'],
    () => api.listTeamMembers(orgId ?? '', teamId ?? ''),
    Boolean(orgId) && Boolean(teamId),
  );
}

export function useTeamMutation<V, R>(orgId: string | undefined, fn: (vars: V) => Promise<R>) {
  return useRefreshing(fn, [['org', orgId ?? '', 'teams']]);
}

export function useRepositoryTeams(scope: RepoScope) {
  return useQ([...repoKey(scope), 'teams'], () => api.listRepositoryTeams(scope));
}

/* ---------------------------------------------------------------------- *
 * Branches and protection
 * ---------------------------------------------------------------------- */

export function useBranchPage(scope: RepoScope, cursor: string | undefined, q: string) {
  return useQ([...repoKey(scope), 'branches', 'page', cursor ?? '', q], () =>
    api.listBranchPage(scope, { ...(cursor ? { cursor } : {}), ...(q ? { q } : {}) }),
  );
}

/**
 * The branches a source selector offers: the first page of the server's own
 * name filter, as many as one page holds. The key carries the repository, so
 * one repository's options can never answer for another's, and a query that is
 * no longer current is left to the cache rather than rendered.
 */
export const branchOptionsLimit = 100;

export function useBranchOptions(scope: RepoScope, q: string, enabled = true) {
  return useQ(
    [...repoKey(scope), 'branches', 'options', q],
    () => api.listBranchPage(scope, { limit: branchOptionsLimit, ...(q ? { q } : {}) }),
    enabled,
  );
}

export function useProtectionRules(scope: RepoScope) {
  return useQ([...repoKey(scope), 'protection'], () => api.listBranchProtectionRules(scope));
}

/** A change to a repository's branches, rules or access: refresh all of it. */
export function useRepoMutation<V, R>(scope: RepoScope, fn: (vars: V) => Promise<R>) {
  return useRefreshing(fn, [repoKey(scope)]);
}

/* ---------------------------------------------------------------------- *
 * Pull requests
 * ---------------------------------------------------------------------- */

export function usePullChecks(scope: RepoScope, n: number) {
  return useQ(
    [...pullKey(scope, n), 'checks'],
    () => api.listPullRequestChecks(scope, n),
    true,
    10_000,
  );
}

export function usePullTimeline(scope: RepoScope, n: number) {
  return useQ([...pullKey(scope, n), 'timeline'], () => api.listPullRequestTimeline(scope, n));
}

export function usePullLinkedIssues(scope: RepoScope, n: number) {
  return useQ([...pullKey(scope, n), 'linked'], () => api.listPullRequestLinkedIssues(scope, n));
}

/* ---------------------------------------------------------------------- *
 * Issues
 * ---------------------------------------------------------------------- */

export function useIssueSearch(scope: RepoScope, query: api.IssueQuery) {
  return useQ([...repoKey(scope), 'issues', 'search', query], () => api.searchIssues(scope, query));
}

export function useIssueTimeline(scope: RepoScope, n: number) {
  return useQ([...issueKey(scope, n), 'timeline'], () => api.listIssueTimeline(scope, n));
}

export function useIssueLinkedPulls(scope: RepoScope, n: number) {
  return useQ([...issueKey(scope, n), 'linked'], () => api.listIssueLinkedPullRequests(scope, n));
}

export function useLabels(scope: RepoScope) {
  return useQ([...repoKey(scope), 'labels'], () => api.listLabels(scope), true, 60_000);
}

export function useMilestones(scope: RepoScope) {
  return useQ([...repoKey(scope), 'milestones'], () => api.listMilestones(scope), true, 60_000);
}

/* ---------------------------------------------------------------------- *
 * Projects
 * ---------------------------------------------------------------------- */

export function useProjects(orgId: string | undefined, state: 'open' | 'closed' | undefined) {
  return useQ(
    ['org', orgId ?? '', 'projects', state ?? 'all'],
    () => api.listProjects(orgId ?? '', state ? { state } : {}),
    Boolean(orgId),
  );
}

export function useProject(orgId: string | undefined, projectId: string) {
  return useQ(
    projectKey(orgId ?? '', projectId),
    () => api.getProject(orgId ?? '', projectId),
    Boolean(orgId),
  );
}

/**
 * Every item on a board, walking the pages.
 *
 * A board is read whole because it is shown whole; the server bounds a
 * project at 2,000 items, so the walk is bounded too.
 */
export function useProjectItems(orgId: string | undefined, projectId: string, q: string) {
  return useQ(
    [...projectKey(orgId ?? '', projectId), 'items', q],
    async () => {
      const all: api.ProjectItem[] = [];
      let cursor: string | undefined;
      for (let page = 0; page < 25; page++) {
        const result = await api.listProjectItems(orgId ?? '', projectId, {
          limit: 100,
          ...(cursor ? { cursor } : {}),
          ...(q ? { q } : {}),
        });
        all.push(...result.data);
        if (!result.next_cursor) break;
        cursor = result.next_cursor;
      }
      return all;
    },
    Boolean(orgId),
    5_000,
  );
}

export function useProjectActivity(orgId: string | undefined, projectId: string) {
  return useQ(
    [...projectKey(orgId ?? '', projectId), 'activity'],
    () => api.listProjectActivity(orgId ?? '', projectId),
    Boolean(orgId),
  );
}

export function useProjectMutation<V, R>(
  orgId: string | undefined,
  projectId: string,
  fn: (vars: V) => Promise<R>,
) {
  return useRefreshing(fn, [projectKey(orgId ?? '', projectId), ['org', orgId ?? '', 'projects']]);
}

export function useCreateProject(orgId: string | undefined) {
  return useRefreshing(
    (body: { name: string; description?: string }) => api.createProject(orgId ?? '', body),
    [['org', orgId ?? '', 'projects']],
  );
}

/* ---------------------------------------------------------------------- *
 * Inbox, dashboard, search, audit
 * ---------------------------------------------------------------------- */

export function useUnreadCount(orgId: string | undefined) {
  return useQuery<number, ApiError>({
    queryKey: ['org', orgId ?? '', 'notifications', 'unread-count'],
    queryFn: () => api.getNotificationUnreadCount(orgId ?? ''),
    enabled: Boolean(orgId),
    staleTime: 30_000,
    // The badge is a glance; a minute is fresh enough and costs one cheap
    // request per open tab.
    refetchInterval: 60_000,
    retry: retryPolicy,
  });
}

export function useInboxMutation<V, R>(orgId: string | undefined, fn: (vars: V) => Promise<R>) {
  return useRefreshing(fn, [['org', orgId ?? '', 'notifications']]);
}

export function useNotificationPreferences(orgId: string | undefined) {
  return useQ(
    ['org', orgId ?? '', 'notification-preferences'],
    () => api.listNotificationPreferences(orgId ?? ''),
    Boolean(orgId),
  );
}

export function useDashboard(orgId: string | undefined) {
  return useQ(
    ['org', orgId ?? '', 'dashboard'],
    () => api.getDashboard(orgId ?? ''),
    Boolean(orgId),
  );
}

export function useSearch(orgId: string | undefined, type: api.SearchType, q: string) {
  return useQ(
    ['org', orgId ?? '', 'search', type, q],
    () => api.searchOrganization(orgId ?? '', { q, type }),
    Boolean(orgId) && q.trim().length > 0,
    30_000,
  );
}

export function useAuditEvents(orgId: string | undefined, query: api.AuditQuery) {
  return useQ(
    ['org', orgId ?? '', 'audit', query],
    () => api.listAuditEvents(orgId ?? '', query),
    Boolean(orgId),
  );
}
